import { POST_LOGIN_REDIRECT_PATH } from "@/features/auth/constants/auth.constants";

const REDIRECT_STORAGE_KEY = "auth_redirect_path";

/**
 * Reads the redirect path the login page stashed from its `?redirect=` search
 * param and clears it, so a stored destination is only ever used once.
 */
export function consumeRedirectPath(): string | null {
  const stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  if (stored) {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  }
  return stored;
}

export type ResolvedRedirect = { external: string } | { internal: string };

/**
 * Decides where to send the user after auth. An absolute URL is only honored
 * when it points at the configured admin origin (so a superadmin bounces back
 * to the dashboard). Anything else is treated as an internal path to avoid an
 * open redirect.
 */
export function resolveRedirect(redirectPath: string | null): ResolvedRedirect {
  if (!redirectPath) return { internal: POST_LOGIN_REDIRECT_PATH };

  const adminUrl = import.meta.env.VITE_ADMIN_URL;
  if (adminUrl) {
    try {
      const target = new URL(redirectPath);
      if (target.origin === new URL(adminUrl).origin) {
        return { external: target.toString() };
      }
    } catch {
      // Not an absolute URL - fall through to internal handling.
    }
  }

  return { internal: redirectPath.startsWith("/") ? redirectPath : POST_LOGIN_REDIRECT_PATH };
}
